import { useTranslation } from "react-i18next";

import type { DashboardView } from "@/hooks/useDashboardView";

interface DashboardViewToggleProps {
  view: DashboardView;
  onChange: (next: DashboardView) => void;
  /**
   * Stretch to the container instead of hugging its labels. Used in the
   * mobile slot, where the control is a full-width band under the greeting.
   */
  fullWidth?: boolean;
}

/**
 * Segmented "Vista simple / Vista avanzada" control in the dashboard header.
 * Styled by `.fx-view-toggle`, which mirrors the `.fx-acct-mode-toggle`
 * segmented control used on the accountant onboarding surfaces.
 */
export function DashboardViewToggle({
  view,
  onChange,
  fullWidth = false,
}: DashboardViewToggleProps): JSX.Element {
  const { t } = useTranslation();

  const options: { id: DashboardView; label: string }[] = [
    { id: "simple", label: t("dashboard.view.simple", "Simple") },
    { id: "advanced", label: t("dashboard.view.advanced", "Avanzada") },
  ];

  // Drives the sliding thumb in CSS (.fx-view-toggle::before). Kept as an
  // index rather than a class so the pill animates between positions instead
  // of the background jumping from one button to the other.
  const activeIndex = options.findIndex((o) => o.id === view);

  return (
    <div
      className={`fx-view-toggle${fullWidth ? " is-full" : ""}`}
      role="group"
      aria-label={t("dashboard.view.groupLabel", "Vista del panel")}
      style={
        {
          "--fx-view-index": activeIndex < 0 ? 0 : activeIndex,
        } as React.CSSProperties
      }
    >
      {options.map((option) => (
        <button
          key={option.id}
          type="button"
          onClick={() => onChange(option.id)}
          aria-pressed={view === option.id}
          className={`fx-view-toggle-btn${view === option.id ? " is-active" : ""}`}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}
